import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MeetingInsightsDto } from '@interfaces/meetings/responses/meeting-insights.response';
import { MeetingTranscriptSegmentDto } from '@interfaces/meetings/responses/meeting.response';
import { MeetingClientDto } from '@interfaces/meetings/responses/meeting-visibility.response';
import { MeetingsClientHttpService } from '@services/http/meetings/meetings-client-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { first } from 'rxjs';
import {
  formatarDuracaoDaLigacao,
  mensagemDoErro,
} from '@features/dashboard/meetings/components/meeting-presenter';
import {
  displayTitle,
  formatSecondsAsClock,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';

/**
 * Detalhe de uma call para o usuário de cliente (card 3803, `/dashboard/client-meetings/:id`), só
 * leitura: título, descrição, data, participantes, vídeo, resumo do Cog e transcrição.
 *
 * **Pronta e desligada**, como a lista (`clientMeetingsGuard`). Tudo vem do recorte do cliente
 * (`api/v1/client/meetings/{id}`): nada de vínculos internos, motivo da IA, histórico ou dado de outra
 * empresa. Call fora da regra responde "não encontrada", e a tela diz isso sem dar pista de que existe.
 */
@Component({
  selector: 'app-client-meeting-detail',
  standalone: true,
  // `RouterLink` explícito: o `SharedModule` não reexporta o `RouterModule`.
  imports: [SharedModule, RouterLink],
  templateUrl: './client-meeting-detail.component.html',
  styleUrl: './client-meeting-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientMeetingDetailComponent implements OnInit {
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  protected readonly meeting = signal<MeetingClientDto | null>(null);
  protected readonly insights = signal<MeetingInsightsDto | null>(null);
  protected readonly segments = signal<MeetingTranscriptSegmentDto[]>([]);
  protected readonly videoUrl = signal<string | null>(null);
  /** O vídeo não pôde ser aberto (arquivo indisponível ou falha): sai do "Carregando" e diz isso. */
  protected readonly videoUnavailable = signal(false);
  /** A transcrição já respondeu (com ou sem trechos, ou com falha): sai do "Carregando". */
  protected readonly transcriptDone = signal(false);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly title = computed(() => {
    const m = this.meeting();
    return m ? displayTitle(m) : 'Call';
  });

  protected readonly breadcrumbs = computed<MenuItem[]>(() => [
    { label: 'Calls', routerLink: '/dashboard/client-meetings' },
    { label: this.title() },
  ]);

  protected readonly duration = formatarDuracaoDaLigacao;
  protected readonly clock = formatSecondsAsClock;

  private meetingId = '';
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);

  constructor(private readonly clientMeetings: MeetingsClientHttpService) {}

  ngOnInit(): void {
    this.meetingId = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.clientMeetings
      .getMeeting(this.meetingId)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: meeting => {
          this.loading.set(false);
          this.meeting.set(meeting);
          if (meeting) this.loadExtras(meeting);
        },
        error: error => {
          this.loading.set(false);
          this.error.set(
            mensagemDoErro(error, 'Call não encontrada. Volte para a lista de calls.')
          );
        },
      });
  }

  /** Vídeo (link temporário), resumo e transcrição; falha em um não derruba a tela. */
  private loadExtras(meeting: MeetingClientDto): void {
    if (meeting.hasRecording) {
      this.clientMeetings
        .getRecordingUrl(meeting.id)
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: result => {
            this.videoUrl.set(result?.url ?? null);
            this.videoUnavailable.set(!result?.url);
          },
          error: () => {
            this.videoUrl.set(null);
            this.videoUnavailable.set(true);
          },
        });
    }
    if (meeting.hasTranscript) {
      this.clientMeetings
        .getTranscript(meeting.id)
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: transcript => {
            this.segments.set(transcript?.segments ?? []);
            this.transcriptDone.set(true);
          },
          error: () => {
            this.segments.set([]);
            this.transcriptDone.set(true);
          },
        });
      this.clientMeetings
        .getInsights(meeting.id)
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: insights => this.insights.set(insights),
          error: () => this.insights.set(null),
        });
    }
  }
}
